import { MutationCache, QueryClient } from "@tanstack/react-query";
import { reportMutationError } from "./mutationErrorBridge";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /**
       * Affiche automatiquement un toast d'erreur si la mutation échoue.
       * Opt-in DÉLIBÉRÉ : plusieurs appels passent déjà leur propre
       * `onError` avec un toast, et un handler global inconditionnel
       * afficherait deux toasts pour la même erreur.
       */
      toastOnError?: boolean;
    };
  }
}

/**
 * React Query client unique pour toute l'app.
 *
 * Valeurs par défaut :
 * - staleTime 30s : les données sont "fraîches" pendant 30s avant qu'un
 *   refetch automatique soit déclenché.
 * - gcTime 5 min : les données inutilisées sont gardées en cache 5 min
 *   avant d'être nettoyées (utile pour la navigation rapide entre pages).
 * - retry 1 : un seul réessai en cas d'erreur réseau.
 * - refetchOnWindowFocus false : évite les refetches intempestifs quand
 *   l'utilisateur change d'onglet.
 */
export const queryClient = new QueryClient({
  // Filet global : toute mutation marquée `meta.toastOnError` remonte son
  // échec à l'utilisateur. Sans ça, un refus de la base (garde-fou de
  // publication, permissions, RLS) était avalé en silence — cf.
  // src/lib/mutationErrorBridge.ts.
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.meta?.toastOnError) reportMutationError(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 300_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});
